import { useEffect, useRef, useState } from 'react'
import { api, type MeResponse } from './lib/api'
import { Login } from './auth/Login'
import { MailboxManager, type MailboxManagerHandle } from './MailboxManager'

type Theme = 'light' | 'dark'

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'
  const stored = window.localStorage.getItem('theme')
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export default function App() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [linkBanner, setLinkBanner] = useState<string | null>(null)
  const [refreshingAll, setRefreshingAll] = useState(false)
  const [refreshProgress, setRefreshProgress] = useState<{ done: number; total: number } | null>(null)
  const [refreshingSize, setRefreshingSize] = useState(false)
  const [sizeProgress, setSizeProgress] = useState<{ done: number; total: number } | null>(null)
  const [deletingAll, setDeletingAll] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null)
  const [theme, setTheme] = useState<Theme>(getInitialTheme)
  const managerRef = useRef<MailboxManagerHandle>(null)

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') root.classList.add('dark')
    else root.classList.remove('dark')
    try {
      window.localStorage.setItem('theme', theme)
    } catch {}
  }, [theme])

  function toggleTheme() {
    setTheme(t => (t === 'dark' ? 'light' : 'dark'))
  }

  async function refreshAll() {
    setRefreshingAll(true)
    setRefreshProgress({ done: 0, total: 0 })
    try {
      await managerRef.current?.refreshAll((done, total) => {
        setRefreshProgress({ done, total })
      })
    } finally {
      setRefreshingAll(false)
      setRefreshProgress(null)
    }
  }

  async function refreshSize() {
    setRefreshingSize(true)
    setSizeProgress({ done: 0, total: 0 })
    try {
      await managerRef.current?.refreshAllSizes((done, total) => {
        setSizeProgress({ done, total })
      })
    } finally {
      setRefreshingSize(false)
      setSizeProgress(null)
    }
  }

  async function deleteAll() {
    setDeletingAll(true)
    setDeleteProgress({ done: 0, total: 0 })
    try {
      await managerRef.current?.deleteAll((done, total) => {
        setDeleteProgress({ done, total })
      })
    } finally {
      setDeletingAll(false)
      setDeleteProgress(null)
    }
  }

  useEffect(() => {
    api.me().then(setMe).finally(() => setLoading(false))

    const params = new URLSearchParams(window.location.search)
    if (params.get('ms') === 'linked') {
      setLinkBanner('Microsoft account linked.')
      cleanQuery()
    } else if (params.get('ms') === 'error') {
      setLinkBanner(`Microsoft link failed: ${params.get('reason') ?? 'unknown'}`)
      cleanQuery()
    }
  }, [])

  async function refresh() {
    setMe(await api.me())
  }

  async function logout() {
    await api.logout()
    setMe(null)
  }

  const [linking, setLinking] = useState(false)

  async function startMicrosoftLink() {
    setLinking(true)
    try {
      const url = await api.beginMicrosoftLink()
      window.location.assign(url)
    } catch (err) {
      console.error('[link-ms] failed to start', err)
      setLinking(false)
      setLinkBanner(
        `Couldn't start Microsoft link: ${err instanceof Error ? err.message : 'unknown'}`
      )
    }
  }

  async function unlinkMicrosoft() {
    if (!confirm('Unlink your Microsoft account? Your mailbox list stays, but you won\'t see messages until you relink.')) {
      return
    }
    await api.microsoftUnlink()
    await refresh()
  }

  if (loading) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>
  }

  if (!me) {
    return <Login onSuccess={refresh} theme={theme} onToggleTheme={toggleTheme} />
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3 dark:border-slate-800">
        <div className="flex items-center gap-4">
          <div className="font-semibold">SharedMailbox Viewer</div>
          {me.microsoftLinked && (
            <div className="flex items-center gap-2">
              <button
                onClick={refreshAll}
                disabled={refreshingAll}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
                title="Refresh counts and messages for every mailbox"
              >
                {refreshingAll
                  ? refreshProgress && refreshProgress.total > 0
                    ? `Refreshing ${refreshProgress.done}/${refreshProgress.total}…`
                    : 'Refreshing…'
                  : 'Refresh All'}
              </button>
              <button
                onClick={refreshSize}
                disabled={refreshingSize}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
                title="Sum message sizes across all folders for every mailbox. Auto-runs daily."
              >
                {refreshingSize
                  ? sizeProgress && sizeProgress.total > 0
                    ? `Sizing ${sizeProgress.done}/${sizeProgress.total}…`
                    : 'Sizing…'
                  : 'Refresh Size'}
              </button>
              <button
                onClick={deleteAll}
                disabled={deletingAll}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-rose-500 hover:text-rose-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-rose-500 dark:hover:text-rose-400"
                title="Move every message in every mailbox to Deleted Items"
              >
                {deletingAll
                  ? deleteProgress && deleteProgress.total > 0
                    ? `Deleting ${deleteProgress.done}/${deleteProgress.total}…`
                    : 'Deleting…'
                  : 'Delete All'}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-600 dark:text-slate-400">{me.user.email}</span>
          {me.microsoft && (
            <span className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-400">
              MS: {me.microsoft.email}
              <button
                onClick={unlinkMicrosoft}
                className="ml-2 text-slate-500 hover:text-rose-500 dark:text-slate-500 dark:hover:text-rose-400"
                title="Unlink Microsoft"
              >
                ✕
              </button>
            </span>
          )}
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <button
            onClick={logout}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Sign out
          </button>
        </div>
      </header>

      {linkBanner && (
        <div className="border-b border-slate-200 bg-slate-100 px-6 py-2 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-300">
          {linkBanner}
          <button
            onClick={() => setLinkBanner(null)}
            className="ml-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            ✕
          </button>
        </div>
      )}

      <main className="flex-1 overflow-hidden p-6">
        {!me.microsoftLinked ? (
          <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/60 dark:shadow-none">
            <h2 className="text-lg font-semibold">Connect your Microsoft 365 account</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              To view your shared mailboxes you'll need to link the Microsoft account that has delegate access.
              You'll be redirected to Microsoft to grant <code className="text-slate-800 dark:text-slate-300">Mail.Read.Shared</code> /
              <code className="text-slate-800 dark:text-slate-300"> Mail.ReadWrite.Shared</code>.
            </p>
            <button
              type="button"
              onClick={startMicrosoftLink}
              disabled={linking}
              className="mt-4 inline-block rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
            >
              {linking ? 'Redirecting to Microsoft…' : 'Link Microsoft account'}
            </button>
          </div>
        ) : (
          <MailboxManager ref={managerRef} />
        )}
      </main>
    </div>
  )
}

export function ThemeToggle({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  const next = theme === 'dark' ? 'light' : 'dark'
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-center rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  )
}

function SunIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4.95 2.05a1 1 0 010 1.414l-.707.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM18 9a1 1 0 110 2h-1a1 1 0 110-2h1zm-2.464 5.536a1 1 0 010 1.414l-.707.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM10 16a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm-5.536-1.464a1 1 0 011.414 0l.707.707a1 1 0 11-1.414 1.414l-.707-.707a1 1 0 010-1.414zM3 9a1 1 0 110 2H2a1 1 0 110-2h1zm2.05-4.95a1 1 0 011.414 1.414l-.707.707A1 1 0 014.343 4.757l.707-.707zM10 6a4 4 0 100 8 4 4 0 000-8z" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
    </svg>
  )
}

function cleanQuery() {
  const url = new URL(window.location.href)
  url.search = ''
  window.history.replaceState({}, '', url.toString())
}
